import { sampleProject } from "../data/sampleProject";
import type {
  AgentBeat,
  BlockingPlan,
  Character,
  EvoLinkCategory,
  ExportScope,
  FilmProject,
  ProjectGoal,
  Scene,
  StoryboardFrame,
  UploadedAssetRecord,
  UploadedAssetManifest
} from "../types";
import { chunkForFrame, frameBlockedByDisabledChunk, framesForScope, sceneChunks } from "./storyboard";

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value && typeof value === "object" && !Array.isArray(value));
}

function asArray(value: unknown): unknown[] {
  return Array.isArray(value) ? value : [];
}

function asRecordArray(value: unknown): Record<string, unknown>[] {
  return asArray(value).filter(isRecord);
}

function asString(value: unknown, fallback = ""): string {
  return typeof value === "string" && value.trim() ? value.trim() : fallback;
}

function asNumber(value: unknown, fallback = 0): number {
  const number = typeof value === "number" ? value : Number(value);
  return Number.isFinite(number) ? number : fallback;
}

function asBoolean(value: unknown, fallback = false): boolean {
  if (value === undefined || value === null) return fallback;
  if (typeof value === "boolean") return value;
  return ["1", "true", "yes", "on"].includes(String(value).trim().toLowerCase());
}

export function runtimeToSeconds(value: unknown, fallback = 0): number {
  if (typeof value === "number" && Number.isFinite(value)) return Math.round(value);
  const text = String(value || "").trim();
  if (!text) return fallback;
  if (text.includes(":")) {
    const parts = [...text.matchAll(/\d+/g)].map((match) => Number(match[0]));
    if (parts.length === 2) return parts[0] * 60 + parts[1];
    if (parts.length === 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
  }
  const number = Number(text.match(/\d+(?:\.\d+)?/)?.[0] || "");
  return Number.isFinite(number) ? Math.round(number) : fallback;
}

export function secondsToRuntime(seconds: number): string {
  const safe = Math.max(0, Math.round(seconds || 0));
  const minutes = Math.floor(safe / 60);
  const remainder = safe % 60;
  return `${minutes}:${String(remainder).padStart(2, "0")}`;
}

function uploadedAssets(value: unknown): UploadedAssetManifest {
  const source = isRecord(value) ? value : {};
  return {
    characterCards: assetRecords(source.characterCards, "character_card"),
    sceneReferences: assetRecords(source.sceneReferences, "scene_reference"),
    voiceAudio: assetRecords(source.voiceAudio, "voice_audio"),
    music: assetRecords(source.music, "music"),
    sfx: assetRecords(source.sfx, "sfx"),
    videoReferences: assetRecords(source.videoReferences, "video_reference"),
    styleRefs: assetRecords(source.styleRefs, "style_reference"),
    branding: assetRecords(source.branding, "branding"),
    documents: assetRecords(source.documents, "document")
  };
}

function assetRecords(value: unknown, fallbackKind: string): UploadedAssetRecord[] {
  return asRecordArray(value).map((item, index) => {
    const parsedReference = isRecord(item.parsedReference) ? item.parsedReference : undefined;
    return {
      assetId: asString(item.assetId, `${fallbackKind}-${index + 1}`),
      projectId: asString(item.projectId),
      kind: asString(item.kind, fallbackKind),
      label: asString(item.label || item.fileName, `${fallbackKind.replace(/_/g, " ")} ${index + 1}`),
      fileName: asString(item.fileName, `${fallbackKind}-${index + 1}`),
      mimeType: asString(item.mimeType),
      size: asNumber(item.size, 0),
      sourceUrl: asString(item.sourceUrl),
      storageKey: asString(item.storageKey),
      cloudStatus: asString(item.cloudStatus),
      cloudMessage: asString(item.cloudMessage),
      localOnly: Boolean(item.localOnly),
      sourceOfTruth: item.sourceOfTruth !== false,
      locked: item.locked !== false,
      disabled: Boolean(item.disabled),
      sourceMode: asString(item.sourceMode) as UploadedAssetRecord["sourceMode"],
      provider: asString(item.provider) as UploadedAssetRecord["provider"],
      uploadedAt: asString(item.uploadedAt),
      sceneId: asString(item.sceneId),
      characterName: asString(item.characterName),
      parseStatus: asString(item.parseStatus) as UploadedAssetRecord["parseStatus"],
      parsedReference: parsedReference as UploadedAssetRecord["parsedReference"],
      parseError: asString(item.parseError),
      parsedAt: asString(item.parsedAt)
    };
  });
}

function missingItemsByLabel(analysis: Record<string, unknown>): Record<string, Set<string>> {
  const missing: Record<string, Set<string>> = {};
  for (const item of asRecordArray(analysis.missingAssets)) {
    const label = asString(item.label).toLowerCase();
    missing[label] = new Set(asArray(item.items).map((value) => asString(value)).filter(Boolean));
  }
  return missing;
}

function sceneReadiness(scene: Record<string, unknown>, missing: Record<string, Set<string>>): Scene["readiness"] {
  const characters = asArray(scene.characters).map((value) => asString(value)).filter(Boolean);
  const location = asString(scene.location);
  if (characters.some((character) => missing["character cards"]?.has(character))) return "needs_character";
  if (location && missing["scene references"]?.has(location)) return "missing_reference";
  if (characters.some((character) => missing["voice audio"]?.has(character))) return "needs_voice";
  return "ready";
}

function frameSource(provider: string, image: Record<string, unknown> | null): StoryboardFrame["source"] {
  const status = asString(image?.status);
  if (provider === "user_upload" || status === "uploaded") return "uploaded";
  if (status === "selected" || status === "skipped") return "current";
  if (provider === "chatgpt_image" && asString(image?.status) === "generated") return "generated";
  if (provider === "chatgpt_image") return "current";
  return "internal";
}

function frameGenerationStatus(provider: string, image: Record<string, unknown> | null): StoryboardFrame["generationStatus"] {
  const status = asString(image?.status);
  if (provider === "user_upload" || status === "uploaded") return "uploaded";
  if (status === "selected" || status === "skipped") return "selected";
  if (provider === "chatgpt_image" && status === "generated") return "generated";
  if (provider === "chatgpt_image" && status === "error") return "failed";
  if (provider === "chatgpt_image") return "prompt_ready";
  if (provider === "internal_svg") return "saved";
  return "prompt_ready";
}

function blockingField(value: Record<string, unknown>, camelKey: string, snakeKey: string, fallback = ""): string {
  return asString(value[camelKey] || value[snakeKey], fallback);
}

function normalizeBlockingPlan(value: unknown): BlockingPlan | undefined {
  if (!isRecord(value)) return undefined;
  const characterMarks = asRecordArray(value.characterMarks || value.character_marks)
    .map((mark) => ({
      character: blockingField(mark, "character", "character"),
      framePosition: blockingField(mark, "framePosition", "frame_position"),
      depth: blockingField(mark, "depth", "depth"),
      bodyOrientation: blockingField(mark, "bodyOrientation", "body_orientation"),
      eyelineTarget: blockingField(mark, "eyelineTarget", "eyeline_target"),
      action: blockingField(mark, "action", "action")
    }))
    .filter((mark) => mark.character || mark.framePosition || mark.action);
  const plan: BlockingPlan = {
    characterMarks,
    objectMark: blockingField(value, "objectMark", "object_mark"),
    movementPath: blockingField(value, "movementPath", "movement_path"),
    cameraAxis: blockingField(value, "cameraAxis", "camera_axis"),
    screenDirection: blockingField(value, "screenDirection", "screen_direction"),
    startFrame: blockingField(value, "startFrame", "start_frame"),
    endFrame: blockingField(value, "endFrame", "end_frame"),
    storyChange: blockingField(value, "storyChange", "story_change"),
    continuityRisk: blockingField(value, "continuityRisk", "continuity_risk")
  };
  return characterMarks.length || Object.entries(plan).some(([key, item]) => key !== "characterMarks" && Boolean(item))
    ? plan
    : undefined;
}

function fallbackBlockingPlan(scene: Scene, shotType: string, label: string): BlockingPlan {
  const lowerShot = shotType.toLowerCase();
  const isInsert = lowerShot.includes("insert") || lowerShot.includes("detail") || lowerShot.includes("close");
  const isWide = lowerShot.includes("wide") || lowerShot.includes("establishing") || lowerShot.includes("aerial");
  const subject = label || scene.location || scene.slugline;
  const characters = scene.characters.length ? scene.characters.slice(0, 4) : ["Lead"];
  return {
    characterMarks: characters.map((character, index) => {
      const side = index % 2 === 0 ? "camera-left third" : "camera-right third";
      return {
        character,
        framePosition: isInsert && index === 0 ? "hands enter foreground" : side,
        depth: isWide ? "midground" : "foreground",
        bodyOrientation: isInsert ? "profile to prop" : "three-quarter toward the active story object",
        eyelineTarget: subject,
        action: isInsert ? "reveals the object without covering it" : "holds a readable mark through the beat turn"
      };
    }),
    objectMark: isInsert ? `${subject} locked center foreground` : `${subject} held center-right`,
    movementPath: isWide ? "left-to-right drift, then settle" : isInsert ? "small hand move, then hold" : "small forward lean, then hold",
    cameraAxis: "keep primary character camera-left looking camera-right",
    screenDirection: "left-to-right continuity",
    startFrame: "beat begins before the decision or reveal",
    endFrame: "beat ends with the choice, reveal, or reaction readable",
    storyChange: isInsert ? "object clue becomes active" : "character position changes the story beat",
    continuityRisk: "Preserve eyeline, prop side, movement direction, and end-frame state into the next board"
  };
}

function blockingPlanForFrame(frame: Record<string, unknown>, scene: Scene, shotType: string, label: string): BlockingPlan {
  return normalizeBlockingPlan(frame.blockingPlan || frame.blocking_plan) || fallbackBlockingPlan(scene, shotType, label);
}

function apiFrameIsProtectedSkip(frame: Record<string, unknown>): boolean {
  const image = isRecord(frame.image) ? frame.image : null;
  const provider = asString(frame.provider).toLowerCase();
  const imageStatus = asString(image?.status).toLowerCase();
  const state = asString(frame.state).toLowerCase();
  const skipReason = asString(frame.skipReason || image?.skipReason).toLowerCase();
  return (
    frame.generationEligible === false
      || Boolean(skipReason)
      || asBoolean(frame.locked)
      || asBoolean(frame.disabled)
      || asBoolean(frame.sourceOfTruth)
      || ["user_upload", "use_existing", "existing"].includes(provider)
      || ["uploaded", "skipped", "disabled"].includes(imageStatus)
      || ["skipped", "disabled"].includes(state)
  );
}

function storyboardFrameFromApi(frame: Record<string, unknown>, scene: Scene, index: number, fallbackDuration: number): StoryboardFrame {
  const image = isRecord(frame.image) ? frame.image : null;
  const provider = asString(frame.provider, "internal_svg");
  const sourceUrl = asString(frame.sourceUrl || image?.sourceUrl || frame.src);
  const storageKey = asString(frame.storageKey || image?.storageKey);
  const shotType = asString(frame.shot, "Storyboard frame");
  const label = asString(frame.label || frame.sceneLabel, scene.slugline);
  return {
    id: asString(frame.frameId || frame.id, `${scene.id}-frame-${String(index + 1).padStart(2, "0")}`),
    sceneId: scene.id,
    label,
    shotType,
    durationSeconds: asNumber(frame.durationSeconds, fallbackDuration),
    imageUrl: sourceUrl || asString(frame.src, sampleProject.scenes[0]?.frames[0]?.imageUrl || ""),
    sourceUrl: sourceUrl || undefined,
    storageKey: storageKey || null,
    fileName: asString(frame.fileName || image?.fileName) || undefined,
    cloudStatus: asString(frame.cloudStatus || image?.cloudStatus) || undefined,
    cloudMessage: asString(frame.cloudMessage || image?.cloudMessage) || undefined,
    source: frameSource(provider, image),
    locked: false,
    queuedRegenerate: false,
    disabled: false,
    generationStatus: frameGenerationStatus(provider, image),
    generationError: asString(image?.error),
    generatedAt: asString(image?.generatedAt),
    prompt: asString(frame.prompt),
    blockingPlan: blockingPlanForFrame(frame, scene, shotType, label)
  };
}

function fallbackFrames(scene: Scene, shots: Record<string, unknown>[]): StoryboardFrame[] {
  const sceneShots = shots.filter((shot) => asString(shot.sceneId) === scene.id || asString(shot.scene) === `Scene ${String(scene.number).padStart(2, "0")}`);
  const usableShots = sceneShots.length ? sceneShots : [{ type: "WS · Establishing", subject: scene.location }];
  const duration = Math.max(2, Math.round(scene.durationSeconds / Math.max(1, usableShots.length)));
  return usableShots.slice(0, 6).map((shot, index) => {
    const shotType = asString(shot.type, "Coverage");
    const label = asString(shot.subject, scene.slugline);
    return {
      id: `${scene.id}-frame-${String(index + 1).padStart(2, "0")}`,
      sceneId: scene.id,
      label,
      shotType,
      durationSeconds: duration,
      imageUrl: sampleProject.scenes[index % sampleProject.scenes.length]?.frames[0]?.imageUrl || sampleProject.scenes[0].frames[0].imageUrl,
      source: "internal",
      locked: false,
      queuedRegenerate: false,
      disabled: false,
      generationStatus: "prompt_ready",
      prompt: `${shotType} for ${scene.slugline}`,
      blockingPlan: normalizeBlockingPlan(shot.blockingPlan || shot.blocking_plan) || fallbackBlockingPlan(scene, shotType, label)
    };
  });
}

function agentBeatsFromAnalysis(analysis: Record<string, unknown>, scenes: Scene[], goal: ProjectGoal): AgentBeat[] {
  const agentPlan = isRecord(analysis.agentPlan) ? analysis.agentPlan : {};
  const beats = asRecordArray(agentPlan.beats);
  if (beats.length) {
    return beats.map((beat, index) => {
      const sceneId = asString(beat.sceneId, scenes[index % Math.max(1, scenes.length)]?.id || "");
      return {
        id: asString(beat.id, `beat-${index + 1}`),
        label: asString(beat.label || beat.beatType, `Beat ${index + 1}`),
        sceneId,
        durationSeconds: asNumber(beat.durationSeconds, 8),
        purpose: asString(beat.purpose || beat.description, "Storyboard beat"),
        readiness: asString(beat.readiness) === "needs_input" ? "needs_input" : "ready"
      };
    });
  }
  return scenes.slice(0, goal === "trailer" ? 6 : 8).map((scene, index) => ({
    id: `beat-${scene.id}`,
    label: index === 0 ? "Hook" : index === scenes.length - 1 ? "Final stinger" : `Beat ${index + 1}`,
    sceneId: scene.id,
    durationSeconds: Math.min(12, Math.max(6, Math.round(scene.durationSeconds / 4))),
    purpose: scene.synopsis,
    readiness: scene.readiness === "ready" ? "ready" : "needs_input"
  }));
}

export function analysisToFilmProject(analysis: Record<string, unknown>, previous: FilmProject): FilmProject {
  const project = isRecord(analysis.project) ? analysis.project : {};
  const sourceDocuments = isRecord(analysis.sourceDocuments) ? analysis.sourceDocuments : {};
  const storyboardFrames = asRecordArray(analysis.storyboardFrames);
  const shots = asRecordArray(analysis.shots);
  const missing = missingItemsByLabel(analysis);
  const scenes = asRecordArray(analysis.scenes).map((apiScene, index) => {
    const sceneId = asString(apiScene.sceneId || apiScene.id, `scene_${String(index + 1).padStart(3, "0")}`);
    const durationSeconds = runtimeToSeconds(apiScene.runtime, Math.max(20, Math.round((asNumber(project.runtimeSeconds, previous.runtimeSeconds) || 60) / Math.max(1, asRecordArray(analysis.scenes).length))));
    const scene: Scene = {
      id: sceneId,
      number: asNumber(apiScene.id, index + 1),
      slugline: asString(apiScene.heading, `Scene ${index + 1}`),
      page: index + 1,
      durationSeconds,
      synopsis: asString(apiScene.summary, "Parsed scene"),
      characters: asArray(apiScene.characters).map((value) => asString(value)).filter(Boolean),
      location: asString(apiScene.location, "Primary Location"),
      readiness: "ready",
      frames: []
    };
    scene.readiness = sceneReadiness(apiScene, missing);
    const apiFrames = storyboardFrames.filter((frame) => asString(frame.sceneId) === scene.id || asString(frame.scene) === `Scene ${String(scene.number).padStart(2, "0")}`);
    const frameDuration = Math.max(2, Math.round(durationSeconds / Math.max(1, apiFrames.length || 3)));
    scene.frames = apiFrames.length
      ? apiFrames.slice(0, 12).map((frame, frameIndex) => storyboardFrameFromApi(frame, scene, frameIndex, frameDuration))
      : fallbackFrames(scene, shots);
    return scene;
  });
  const nextScenes = scenes.length ? scenes : previous.scenes;
  const missingCharacterCards = missing["character cards"] || new Set<string>();
  const missingVoiceAudio = missing["voice audio"] || new Set<string>();
  const characters: Character[] = asArray(analysis.characters).map((value) => {
    const name = asString(value, "Lead Role");
    return {
      id: name.toLowerCase().replace(/[^a-z0-9]+/g, "-") || "character",
      name,
      role: "Parsed character",
      cardStatus: missingCharacterCards.has(name) ? "missing" : "uploaded",
      voiceStatus: missingVoiceAudio.has(name) ? "missing" : "assigned"
    };
  });
  const goal = asString(project.goal, previous.goal) as ProjectGoal;
  return {
    ...previous,
    title: asString(project.title, previous.title),
    genre: asString(project.genre, previous.genre || "drama"),
    goal,
    runtimeSeconds: asNumber(project.runtimeSeconds, previous.runtimeSeconds),
    scriptExcerpt: asString(sourceDocuments.scriptText, previous.scriptExcerpt),
    uploadedAssets: uploadedAssets(analysis.uploadedAssets || previous.uploadedAssets),
    apiPlan: analysis,
    scenes: nextScenes,
    characters: characters.length ? characters : previous.characters,
    agentBeats: agentBeatsFromAnalysis(analysis, nextScenes, goal)
  };
}

interface StoryboardMergeOptions {
  targetFrameIds?: string[];
}

export function storyboardToFilmProject(storyboard: Record<string, unknown>, previous: FilmProject, options: StoryboardMergeOptions = {}): FilmProject {
  const frames = asRecordArray(storyboard.frames);
  if (!frames.length) return { ...previous, lastStoryboard: storyboard };
  const targetFrameIds = new Set(options.targetFrameIds || []);
  return {
    ...previous,
    lastStoryboard: storyboard,
    stylePreset: asString(storyboard.style, previous.stylePreset),
    scenes: previous.scenes.map((scene) => {
      const apiFrames = frames.filter((frame) => asString(frame.sceneId) === scene.id || asString(frame.sceneLabel) === scene.slugline);
      if (!apiFrames.length) return scene;
      const duration = Math.max(2, Math.round(scene.durationSeconds / Math.max(1, scene.frames.length || apiFrames.length)));
      let apiFrameIndex = 0;
      const apiFramesById = new Map<string, Record<string, unknown>>();
      apiFrames.forEach((frame) => {
        const frameId = asString(frame.frameId || frame.id);
        if (frameId) apiFramesById.set(frameId, frame);
      });
      const consumedApiFrames = new Set<Record<string, unknown>>();
      const mergedFrames = scene.frames.map((existing, index) => {
        const isProtected = existing.locked || existing.source === "uploaded" || existing.disabled;
        const isTarget = !targetFrameIds.size || targetFrameIds.has(existing.id);
        if (isProtected || !isTarget) return existing;
        let apiFrame = apiFramesById.get(existing.id);
        if (!apiFrame) {
          while (
            apiFrameIndex < apiFrames.length
              && (consumedApiFrames.has(apiFrames[apiFrameIndex]) || apiFrameIsProtectedSkip(apiFrames[apiFrameIndex]))
          ) {
            apiFrameIndex += 1;
          }
          apiFrame = apiFrames[apiFrameIndex];
          apiFrameIndex += 1;
        }
        if (!apiFrame) return existing;
        consumedApiFrames.add(apiFrame);
        if (apiFrameIsProtectedSkip(apiFrame)) return existing;
        return {
          ...storyboardFrameFromApi(apiFrame, scene, index, duration),
          id: existing.id,
          locked: false,
          disabled: false
        };
      });

      for (; apiFrameIndex < apiFrames.length; apiFrameIndex += 1) {
        const apiFrame = apiFrames[apiFrameIndex];
        if (consumedApiFrames.has(apiFrame)) continue;
        if (apiFrameIsProtectedSkip(apiFrame)) continue;
        const apiFrameId = asString(apiFrame.frameId || apiFrame.id);
        if (targetFrameIds.size && (!apiFrameId || !targetFrameIds.has(apiFrameId))) continue;
        mergedFrames.push(storyboardFrameFromApi(apiFrame, scene, mergedFrames.length, duration));
      }

      return {
        ...scene,
        frames: mergedFrames.slice(0, Math.max(12, scene.frames.length))
      };
    })
  };
}

function frameSourceAssignment(project: FilmProject, scene: Scene, frame: StoryboardFrame): Record<string, unknown> {
  const sceneReferences = (project.uploadedAssets?.sceneReferences || []).filter((asset) => asset.sceneId === scene.id && !asset.disabled);
  const sceneReferenceAssetIds = sceneReferences
    .map((asset) => asset.assetId)
    .filter((assetId): assetId is string => Boolean(assetId));
  const sceneVoiceNames = scene.characters.filter((name) => {
    const character = project.characters.find((item) => item.name === name);
    return Boolean(character && character.voiceStatus !== "not_needed" && !character.voiceDisabled);
  });
  const assignments = frame.sourceAssignments;
  const characterNames = assignments?.characterNames || scene.characters;
  const voiceCharacterNames = assignments?.voiceCharacterNames || sceneVoiceNames;
  const referenceAssetIds = assignments?.referenceAssetIds || sceneReferenceAssetIds;
  return {
    mode: assignments ? "custom" : "scene_defaults",
    sceneId: scene.id,
    sceneLabel: scene.slugline,
    frameId: frame.id,
    frameLabel: frame.label,
    shot: frame.shotType,
    characterNames,
    voiceCharacterNames,
    referenceAssetIds,
    referenceLabels: sceneReferences
      .filter((asset) => asset.assetId && referenceAssetIds.includes(asset.assetId))
      .map((asset) => asset.label)
  };
}

export function filmProjectToApiPlan(project: FilmProject): Record<string, unknown> {
  const base = isRecord(project.apiPlan) ? project.apiPlan : {};
  const sceneReferences = project.uploadedAssets?.sceneReferences || [];
  const parsedReferences = sceneReferences.filter((asset) => asset.parsedReference && !asset.disabled);
  const characterDetails = project.characters.map((character) => ({
    id: character.id,
    name: character.name,
    role: character.role,
    cardStatus: character.cardStatus,
    cardSource: character.cardSource || (character.cardStatus === "generated" ? "seed_generated" : character.cardStatus === "uploaded" ? "user_upload" : "missing"),
    cardAssetLabel: character.cardAssetLabel || "",
    cardLocked: Boolean(character.cardLocked),
    cardDisabled: Boolean(character.cardDisabled),
    seedCharacterPrompt: character.seedCharacterPrompt || "",
    voiceStatus: character.voiceStatus,
    voiceSource: character.voiceSource || (character.voiceStatus === "assigned" ? "seed_generated" : character.voiceStatus === "not_needed" ? "not_needed" : "missing"),
    voiceAssetLabel: character.voiceAssetLabel || "",
    voicePreset: character.voicePreset || "cinematic_natural",
    voiceDisabled: Boolean(character.voiceDisabled)
  }));
  const frameAssignments = project.scenes.flatMap((scene) =>
    scene.frames.map((frame) => frameSourceAssignment(project, scene, frame))
  );
  return {
    ...base,
    project: {
      ...(isRecord(base.project) ? base.project : {}),
      title: project.title,
      goal: project.goal,
      genre: project.genre || "drama",
      runtimeSeconds: project.runtimeSeconds
    },
    characters: project.characters.map((character) => character.name),
    characterDetails,
    voices: project.characters
      .filter((character) => character.voiceStatus !== "not_needed" && !character.voiceDisabled)
      .map((character) => ({
        id: `voice-${character.id}`,
        characterId: character.id,
        role: character.name,
        name: character.name,
        source: character.voiceSource === "user_upload" ? "user_audio" : "seedance",
        mode: character.voiceSource === "user_upload" ? "User Audio" : "Generated Voice",
        preset: character.voicePreset || "cinematic_natural",
        assetLabel: character.voiceAssetLabel || "",
        status: character.voiceStatus === "assigned" ? "Set" : "Missing"
      })),
    sourceAssignments: {
      characters: characterDetails,
      references: sceneReferences,
      parsedReferences,
      frameAssignments,
      music: project.uploadedAssets?.music || [],
      sfx: project.uploadedAssets?.sfx || [],
      videoReferences: project.uploadedAssets?.videoReferences || [],
      styleRefs: project.uploadedAssets?.styleRefs || [],
      branding: project.uploadedAssets?.branding || [],
      documents: project.uploadedAssets?.documents || []
    },
    locations: [...new Set(project.scenes.map((scene) => scene.location).filter(Boolean))],
    scenes: project.scenes.map((scene) => ({
      id: String(scene.number).padStart(2, "0"),
      sceneId: scene.id,
      heading: scene.slugline,
      location: scene.location,
      runtime: secondsToRuntime(scene.durationSeconds),
      summary: scene.synopsis,
      characters: scene.characters,
      chunks: sceneChunks(scene).map((chunk) => ({
        id: chunk.id,
        sceneId: chunk.sceneId,
        index: chunk.index,
        label: chunk.label,
        runtime: secondsToRuntime(chunk.durationSeconds),
        durationSeconds: chunk.durationSeconds,
        frameIds: chunk.frameIds,
        enabled: chunk.enabled,
        locked: chunk.locked
      })),
      ready: scene.readiness === "ready"
    })),
    chunks: project.scenes.flatMap((scene) =>
      sceneChunks(scene).map((chunk) => ({
        id: chunk.id,
        sceneId: chunk.sceneId,
        sceneLabel: scene.slugline,
        index: chunk.index,
        label: chunk.label,
        runtime: secondsToRuntime(chunk.durationSeconds),
        durationSeconds: chunk.durationSeconds,
        frameIds: chunk.frameIds,
        enabled: chunk.enabled,
        locked: chunk.locked
      }))
    ),
    storyboardFrames: project.scenes.flatMap((scene) =>
      scene.frames.map((frame, index) => {
        const chunk = chunkForFrame(scene, frame);
        const blockedByChunk = frameBlockedByDisabledChunk(scene, frame);
        return {
          index: String(index + 1).padStart(2, "0"),
          scene: `Scene ${String(scene.number).padStart(2, "0")}`,
          sceneId: scene.id,
          sceneLabel: scene.slugline,
          chunkId: chunk?.id || "",
          chunkLabel: chunk?.label || "",
          chunkEnabled: chunk ? chunk.enabled : true,
          shot: frame.shotType,
          refs: frame.source === "uploaded" ? "Uploaded and locked" : "Storyboard frame",
          state: frame.disabled || blockedByChunk ? "disabled" : "ok",
          src: frame.imageUrl,
          sourceUrl: frame.sourceUrl || frame.imageUrl,
          storageKey: frame.storageKey || null,
          cloudStatus: frame.cloudStatus || "",
          cloudMessage: frame.cloudMessage || "",
          generationStatus: frame.generationStatus || "",
          generationJobId: frame.generationJobId || "",
          generationError: frame.generationError || "",
          generatedAt: frame.generatedAt || "",
          prompt: frame.prompt,
          blockingPlan: frame.blockingPlan,
          sourceAssignments: frameSourceAssignment(project, scene, frame),
          provider: frame.source === "uploaded" ? "user_upload" : frame.source === "generated" ? "chatgpt_image" : "internal_svg",
          providerLabel: frame.source === "uploaded" ? "Uploaded Board" : frame.source === "generated" ? "ChatGPT Image" : "Internal SVG Sketch",
          source: frame.source,
          locked: frame.locked,
          queuedRegenerate: frame.queuedRegenerate,
          disabled: frame.disabled || blockedByChunk,
          disabledReason: blockedByChunk ? "chunk_disabled" : frame.disabled ? "frame_disabled" : "",
          fileName: frame.fileName || "",
          include: !frame.disabled && !blockedByChunk
        };
      })
    ),
    uploadedAssets: project.uploadedAssets || uploadedAssets(null)
  };
}

export function scopedApiPlan(project: FilmProject, selectedSceneId: string, scope: ExportScope): Record<string, unknown> {
  const selectedScenes = new Set(framesForScope(project, selectedSceneId, scope).map((frame) => frame.sceneId));
  const plan = filmProjectToApiPlan(project);
  if (scope === "all") return plan;
  const scenes = asRecordArray(plan.scenes).filter((scene) => selectedScenes.has(asString(scene.sceneId)));
  const storyboardFrames = asRecordArray(plan.storyboardFrames).filter((frame) => selectedScenes.has(asString(frame.sceneId)));
  const chunks = asRecordArray(plan.chunks).filter((chunk) => selectedScenes.has(asString(chunk.sceneId)));
  const selectedCharacterNames = new Set(scenes.flatMap((scene) => asArray(scene.characters).map((value) => asString(value)).filter(Boolean)));
  const sourceAssignments = isRecord(plan.sourceAssignments) ? plan.sourceAssignments : {};
  const scopedSourceAssignments = {
    ...sourceAssignments,
    characters: asRecordArray(sourceAssignments.characters).filter((character) => selectedCharacterNames.has(asString(character.name))),
    references: asRecordArray(sourceAssignments.references).filter((asset) => selectedScenes.has(asString(asset.sceneId))),
    parsedReferences: asRecordArray(sourceAssignments.parsedReferences).filter((asset) => selectedScenes.has(asString(asset.sceneId))),
    frameAssignments: asRecordArray(sourceAssignments.frameAssignments).filter((assignment) => selectedScenes.has(asString(assignment.sceneId))),
    music: asArray(sourceAssignments.music),
    sfx: asArray(sourceAssignments.sfx)
  };
  const voices = asRecordArray(plan.voices).filter((voice) => selectedCharacterNames.has(asString(voice.name || voice.role)));
  return { ...plan, scenes, storyboardFrames, chunks, voices, sourceAssignments: scopedSourceAssignments };
}

export function scopeToSceneSelection(project: FilmProject, selectedSceneId: string, scope: ExportScope): string {
  if (scope === "all" || scope === "beats" || scope === "selected") return "all";
  return selectedSceneId;
}

export function categoriesToPackageSettings(categories: EvoLinkCategory[]): Record<string, boolean> {
  const enabled = new Set(categories);
  return {
    storyboard: enabled.has("boards"),
    characters: enabled.has("characters"),
    voices: enabled.has("voices"),
    lines: enabled.has("lines"),
    sceneReferences: enabled.has("locations"),
    music: enabled.has("music"),
    sfx: enabled.has("sfx"),
    style: enabled.has("style"),
    colorGrade: enabled.has("colorGrade"),
    aspect: enabled.has("aspect"),
    chunkTiming: enabled.has("chunkTiming")
  };
}
